import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const StaffContainer = styled.div`
  padding-top: ${props => props.theme.sizes.headerHeight};
  max-width: ${props => props.theme.sizes.maxWidth};
  margin: 0 auto;
  padding: 120px 20px 60px;
`;

const Title = styled.h1`
  text-align: center;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 40px;
  font-size: 2.8rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Description = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.lightText};
  margin-bottom: 60px;
  line-height: 1.8;
  font-size: 1.1rem;
  word-break: keep-all;

  .mobile-break {
    display: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    .mobile-break {
      display: inline;
    }
  }
`;

const DoctorSection = styled(motion.div)`
  display: flex;
  gap: 100px;
  align-items: center;
  margin-bottom: 80px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 20px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
    align-items: center;
    gap: 60px;
  }
`;

const DoctorImage = styled.div`
  flex: 0 0 420px;
  margin-top: 20px;
  
  img {
    width: 100%;
    border-radius: 20px;
    box-shadow: ${props => props.theme.shadows.medium};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 0 0 auto;
    width: 100%;
    max-width: 380px;
    margin-top: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-width: 320px;
  }
`;

const DoctorInfo = styled.div`
  flex: 1;
  padding-top: 0;

  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 2.8rem;
    margin-bottom: 15px;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 2.2rem;
    }
  }

  h3 {
    color: ${props => props.theme.colors.secondary};
    font-size: 1.4rem;
    margin-bottom: 45px;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-top: 0;
  }
`;

const CredentialSection = styled.div`
  margin-bottom: 40px;

  h4 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.2rem;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid ${props => props.theme.colors.border};
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 1.1rem;
      margin-bottom: 15px;
      text-align: center;
    }
  }

  ul {
    list-style: none;
    list-style-type: none;
    padding: 0;
    margin: 0;
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      background: #f8f9fa;
      border-radius: 10px;
      padding: 20px 15px;
      margin: 0;
    }
  }

  li {
    color: ${props => props.theme.colors.text};
    margin-bottom: 12px;
    line-height: 1.6;
    padding-left: 0;
    list-style: none;
    list-style-type: none;
    position: relative;
    
    &:before {
      display: none;
    }
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      margin-bottom: 10px;
      text-align: center;
      font-size: 0.95rem;
    }
  }
`;

const StyledText = styled.span`
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    display: inline;
  }
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileText = styled.span`
  display: none;
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: inline;
  }
`;

function Staff() {
  return (
    <StaffContainer>
      <Title>의료진 소개</Title>
      <Description>
        <StyledText>
          서울쥬니어치과의 의료진은 소아치과 전문의로서 아이들의 건강한 미소를 위해 최선을 다하고 있습니다.
        </StyledText>
        <MobileText>
          서울쥬니어치과의 의료진은<br />
          소아치과 전문의로서<br />
          아이들의 건강한 미소를 위해<br />
          최선을 다하고 있습니다.
        </MobileText>
      </Description>

      <DoctorSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <DoctorImage>
          <img src="/images/doctor.jpg" alt="장준혁 대표원장" />
        </DoctorImage>
        <DoctorInfo>
          <h2>장준혁 대표원장</h2>
          <h3>소아치과 전문의</h3>
          
          <CredentialSection>
            <h4>학력 및 경력</h4>
            <ul>
              <li>서울대학교 우등 졸업</li>
              <li>서울대학교 치의학대학원 치의학과 졸업</li>
              <li>서울대학교 치과병원 소아치과 인턴 및 레지던트</li>
              <li>서울대학교 치과대학 소아치과 치의학 석사</li>
              <li>서울대학교 치과대학 소아치과 치의학 박사</li>
              <li>前 춘천예치과 소아치과 원장</li>
              <li>前 서산아이사랑 소아치과 원장</li>
            </ul>
          </CredentialSection>

          <CredentialSection>
            <h4>학회 활동</h4>
            <ul>
              <li>대한소아치과학회 정회원</li>
              <li>세계소아치과학회 정회원</li>
              <li>대한치과교정학회 정회원</li>
              <li>대한치과마취과학회 정회원</li>
            </ul>
          </CredentialSection>
        </DoctorInfo>
      </DoctorSection>
    </StaffContainer>
  );
}

export default Staff; 